/**
 * QR-1b: контракт ссылки-приглашения (тикет QR-1 §3.3, ЕДИНЫЙ носитель токена).
 *
 * Токен живёт РОВНО в двух местах:
 *  1) фрагмент URL приглашения `https://<host>/j/#<token>` — это содержимое
 *     QR и App-Link-интента (браузер фрагмент серверу не шлёт и в Referer не
 *     кладёт, RFC 7231 §5.5.2; Android читает `Uri.fragment`);
 *  2) строковый extra `invite_token` intent-URL кнопки «Открыть в приложении»
 *     на лендинге — Chrome разбирает intent-URL ЛОКАЛЬНО, сетевого запроса
 *     с токеном нет; тот же токен стоит и во фрагменте browser_fallback_url
 *     (см. intentUrl) — это форма (1), она в сеть не уходит.
 * Query-параметры (`?t=`) не порождаются и не читаются НИКОГДА (Android их
 * отвергает — `InviteLink.parse`, зеркало здесь: tokenFromHash читает только hash).
 *
 * Модуль без DOM и сети — чтобы грамматику и сборку URL пинили unit-тесты.
 */

/** Грамматика токена — та же, что INVITE_TOKEN_RE сервера (мусор -> 404). */
export const INVITE_TOKEN_RE = /^[A-Za-z0-9_-]{16,64}$/;

/** Пакет Android-приложения (intent-URL лендинга и assetlinks.json). */
export const ANDROID_PACKAGE = 'com.sousa.location_project';

/** Публичный путь лендинга; он же — путь App Link в манифесте (`/j`, `/j/`). */
export const LANDING_PATH = '/j/';

/**
 * QR-3: путь чата участника в браузере. Токена в нём НЕТ и быть не может —
 * после join участник живёт по member-JWT, а одноразовая ссылка больше не
 * нужна (и не должна остаться в истории браузера).
 */
export const MEMBER_CHAT_PATH = '/j/chat';
/**
 * 🔴 WEB-28: явный ключ от кабинета. В браузере, где есть сессия участника,
 * голый корень уводит в переписку — и форма входа владельца становится
 * недостижимой. Этот адрес говорит развилке «мне нужен кабинет» и работает
 * откуда угодно: из чата, из закладки, после выхода из кабинета.
 */
export const CABINET_QUERY = 'cabinet';
export const CABINET_PATH = `/?${CABINET_QUERY}=1`;

/**
 * Токен из `location.hash` ('#<token>' | '' | null). Кривая грамматика -> null:
 * лендинг попросит открыть ссылку заново БЕЗ сетевого вызова (мусор слать
 * незачем; «нет такого» от «кривой» не отличаем — это решает сервер 404-й).
 */
export function tokenFromHash(hash) {
  if (typeof hash !== 'string' || !hash.startsWith('#')) return null;
  const token = hash.slice(1);
  return INVITE_TOKEN_RE.test(token) ? token : null;
}

/**
 * WEB-29: токен из того, что человек ВСТАВИЛ в поле.
 *
 * 🔴 Зачем это нужно. У приложения с экрана «Домой» на iOS своё хранилище,
 * отдельное от Safari: сессия, рождённая при открытии приглашения в Safari, в
 * приложение НЕ попадает. Значит участник обязан войти один раз ВНУТРИ
 * приложения — а туда ссылку можно только вставить руками.
 *
 * Принимаем три формы того, что реально присылают люди:
 *   1. полную ссылку с фрагментом — `https://…/j/#<токен>`;
 *   2. голый токен — ссылку при пересылке часто обрезают до него;
 *   3. то же самое с пробелами и переносами по краям.
 *
 * 🔴 Токен берём ТОЛЬКО из фрагмента (правило QR-1 §3.3: query не читаем
 * никогда — он уезжает на сервер в логи и в Referer, фрагмент не уезжает).
 * Ссылка, где приглашение положено в query, честно даёт `null`: лучше внятный
 * отказ, чем молчаливый вход по адресу, который мы обещали не читать.
 */
export function tokenFromPasted(text) {
  if (typeof text !== 'string') return null;
  const raw = text.trim();
  if (!raw) return null;
  // Голый токен — самая частая форма после пересылки.
  if (INVITE_TOKEN_RE.test(raw)) return raw;
  const hash = raw.indexOf('#');
  if (hash === -1) return null;
  return tokenFromHash(raw.slice(hash));
}

/** URL приглашения от origin ЛК: сервер отдаёт `url` сам, если PUBLIC_BASE_URL
 *  задан; иначе веб строит от своего origin (одна и та же форма, §3.3). */
export function inviteUrl(origin, token) {
  return `${String(origin).replace(/\/+$/, '')}${LANDING_PATH}#${token}`;
}

/**
 * Chrome intent-URL кнопки «Открыть в приложении» (контракт §3.3 / §5):
 *   intent://<host>/j/#Intent;scheme=https;package=<pkg>;
 *   S.invite_token=<token>;S.browser_fallback_url=<лендинг с токеном>;end
 * `host` — host ЛК (с портом, если есть): на стенде `127.0.0.1:8005`, на бою
 * `wificontrol.ru` — оба формы из allowlist приложения (BuildConfig.INVITE_HOSTS).
 * fallback — тот же лендинг, и вызывающий обязан дать его С ФРАГМЕНТОМ
 * (inviteUrl): приложения нет -> Chrome возвращает человека на ЖИВОЕ
 * приглашение, где есть вход в браузере (QR-3), а не на пустую страницу.
 * 🔴 Голый `/j/` тут был багом: на бою 25.08 он дал ложное «недействительно».
 * Экспозиция от фрагмента нулевая — это форма (1), браузер её не шлёт.
 */
export function intentUrl({ host, token, fallbackUrl }) {
  if (!INVITE_TOKEN_RE.test(token)) throw new Error('intentUrl: токен вне грамматики');
  return (
    `intent://${host}${LANDING_PATH}#Intent;scheme=https;package=${ANDROID_PACKAGE};`
    + `S.invite_token=${encodeURIComponent(token)};`
    + `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`
  );
}

/** Грубая эвристика «это Android-браузер» — только для подсказки на лендинге
 *  (кнопки показываются всегда: контракт не зависит от UA). */
export function isAndroidUa(ua) {
  return /\bAndroid\b/i.test(String(ua ?? ''));
}
